import { Trans, useLingui } from '@lingui/react/macro'
import { Text } from 'designSystem/components/Text'
import type { ReportChartSource } from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/reportViewSchema'
import styled from 'styled-components'

const Row = styled.div`
  display: grid;
  grid-template-columns: minmax(8rem, 1fr) minmax(5rem, 2fr) auto;
  padding-block: 0.375rem;
  font-variant-numeric: tabular-nums;
  align-items: center;
  gap: 0.75rem;
  meter {
    width: 100%;
    height: 0.625rem;
    appearance: none;
    background: var(--muted);
    border: none;
    border-radius: 0.25rem;
  }
  meter::-webkit-meter-bar {
    background: var(--muted);
    border: none;
  }
  meter::-webkit-meter-optimum-value {
    background: var(--chart-1, var(--primary));
  }
  meter::-moz-meter-bar {
    background: var(--chart-1, var(--primary));
  }
`
const Columns = styled.div`
  display: flex;
  gap: 1.25rem;
  overflow-x: auto;
  padding-block: 0.5rem;
  > div {
    flex: 1;
    min-width: 5rem;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.5rem;
    text-align: center;
    font-variant-numeric: tabular-nums;
  }
  svg {
    width: 100%;
    height: 9rem;
    border-bottom: 0.0625rem solid var(--border);
    fill: var(--chart-1, var(--primary));
  }
`
export function ReportChart({
  source,
  chartStyle = 'bar',
}: {
  source: ReportChartSource
  chartStyle?: 'bar' | 'column'
}) {
  const { i18n } = useLingui()
  const max = Math.max(1, ...source.points.map((point) => point.value))
  const missing = source.missing_count
  return (
    <>
      <Text variant='caption' color='muted'>
        {source.unit}
      </Text>
      {source.points.length ? (
        chartStyle === 'column' ? (
          <Columns>
            {source.points.map((point, index) => (
              <div key={index}>
                <Text variant='caption'>
                  {i18n.number(point.value, { maximumFractionDigits: 2 })}
                </Text>
                <svg
                  viewBox='0 0 100 100'
                  preserveAspectRatio='none'
                  role='img'
                  aria-label={`${point.label}: ${i18n.number(point.value)} ${source.unit}`}>
                  <rect
                    x={20}
                    y={100 - (point.value / max) * 100}
                    width={60}
                    height={(point.value / max) * 100}
                    rx={2}
                  />
                </svg>
                <Text variant='caption'>{point.label}</Text>
              </div>
            ))}
          </Columns>
        ) : (
          source.points.map((point, index) => (
            <Row key={index}>
              <Text variant='caption'>{point.label}</Text>
              <meter
                min={0}
                max={max}
                value={point.value}
                aria-label={point.label}
              />
              <Text variant='caption'>
                {i18n.number(point.value, { maximumFractionDigits: 2 })}
              </Text>
            </Row>
          ))
        )
      ) : (
        <Text color='muted'>
          <Trans>No recorded values.</Trans>
        </Text>
      )}
      {missing > 0 ? (
        <Text variant='caption' color='muted'>
          <Trans>Records without values: {missing}</Trans>
        </Text>
      ) : null}
      {source.notes.map((note, index) => (
        <Text key={index} variant='caption' color='muted'>
          {note}
        </Text>
      ))}
      {source.truncated ? (
        <Text variant='caption' color='muted'>
          <Trans>
            This chart is truncated. Open the source record for complete
            results.
          </Trans>
        </Text>
      ) : null}
    </>
  )
}
